'use client'

import { motion, useReducedMotion } from 'motion/react'
import { useId } from 'react'
import { cn } from '../../../lib/cn'

export interface SpotlightTopLeftProps {
  /**
   * Color source from theme variables
   * @default 'primary'
   */
  color?: 'primary' | 'accent' | 'muted' | 'foreground'

  /**
   * Enable entrance animation
   * @default true
   */
  animate?: boolean

  /**
   * Animation delay in seconds (allows hero content to load first)
   * @default 0.5
   */
  animationDelay?: number

  className?: string
}

/**
 * SpotlightTopLeft - Soft spotlight glow from top-left corner
 *
 * A blurred ellipse positioned off-canvas to create a natural
 * diagonal spotlight effect illuminating the hero area.
 *
 * Fixed settings: intensity 0.15, spread 1.0
 * Light mode: 85% opacity, Dark mode: 60% opacity
 */
export function SpotlightTopLeft({
  color = 'primary',
  animate = true,
  animationDelay = 0.5,
  className,
}: SpotlightTopLeftProps = {}) {
  const prefersReducedMotion = useReducedMotion()
  const id = useId()
  const filterId = `spotlight-blur-${id}`
  const colorVar = color === 'muted' ? 'var(--muted-foreground)' : `var(--${color})`

  // Fixed ellipse dimensions
  const baseRx = 1924.71
  const baseRy = 273.501

  return (
    <motion.div
      className={cn('pointer-events-none absolute inset-0 z-0 overflow-hidden', className)}
      initial={animate ? { opacity: 0 } : false}
      animate={animate ? { opacity: 1 } : false}
      transition={{
        duration: prefersReducedMotion ? 0.3 : 1.2,
        delay: animate ? (prefersReducedMotion ? 0 : animationDelay) : 0,
        ease: [0.16, 1, 0.3, 1],
      }}
    >
      <div className="h-full w-full opacity-85 dark:opacity-60">
      <svg
        className="absolute left-0 top-0 h-[169%] w-[138%] -translate-x-1/3 -translate-y-1/3 lg:w-[84%]"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 3787 2842"
        fill="none"
      >
        <defs>
          <filter
            id={filterId}
            x="0.860352"
            y="0.838989"
            width="3785.16"
            height="2840.26"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
            <feGaussianBlur stdDeviation="151" result="effect1_foregroundBlur" />
          </filter>
        </defs>
        <g filter={`url(#${filterId})`}>
          <ellipse
            cx={baseRx}
            cy={baseRy}
            rx={baseRx}
            ry={baseRy}
            transform="matrix(-0.822377 -0.568943 -0.568943 0.822377 3631.88 2291.09)"
            fill={colorVar}
            fillOpacity={0.15}
          />
        </g>
      </svg>
      </div>
    </motion.div>
  )
}
